import SearchBar from "@/components/searchbar";
import Throbber from "@/components/throbber";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import useCountdown from "@/hooks/use-countdown";
import { useDeposit, useSubmitTransactionHash } from "@/hooks/use-mutations";
import { useGetDepositOrder } from "@/hooks/use-queries";
import { useDepositStore } from "@/zustand/store";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, ChevronRight } from "lucide-react";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useSearchParams } from "react-router-dom";
import { z } from "zod";
import { motion } from "framer-motion";
import Copy from "@/components/copy";
import { formatAmount } from "@/lib/utils";

const transactionSchema = z.object({
  transactionHash: z.string().min(1, "Transaction Hash cannot be empty"),
});

const depositSchema = z.object({
  token: z.string().min(1, "Payment method is required"),
  depositAmount: z.string().min(1, "Amount is required"),
});

const Deposit = () => {
  const queryClient = useQueryClient();
  const [isCopied, setIsCopied] = useState(false);
  const { depositOrder } = useDepositStore((state) => state);
  const form = useForm({
    resolver: zodResolver(depositSchema),
    defaultValues: {
      depositAmount: "",
    },
  });
  const transactionForm = useForm({
    resolver: zodResolver(transactionSchema),
  });
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const stepParam = searchParams.get("step");
  const [step, setStep] = useState(parseInt(stepParam, 10) || 1);

  const [transactionState, setTransactionState] = useState("pending");

  const countdown = useCountdown();

  const {
    refetch: refetchDepositOrder,
    data: depositOrderData,
    isSuccess,
  } = useGetDepositOrder({
    id: depositOrder.id,
    enabled: !!depositOrder.id,
  });

  useEffect(() => {
    if (depositOrder.id) {
      refetchDepositOrder;
    }
  }, []);

  useEffect(() => {
    if (depositOrderData && depositOrderData.status === "completed") {
      setTransactionState("success");
      queryClient.invalidateQueries(["user"]);
    }
  }, [depositOrderData]);
  const { mutateAsync: deposit, isPending: isDepositing } = useDeposit({
    onSuccess: () => {
      setStep(2);
    },
  });

  const { mutateAsync: submitTransactionHash, isPending: isSubmittingHash } =
    useSubmitTransactionHash({
      id: depositOrder.id,
      onSuccess: () => {
        setStep(3);
        setTransactionState("pending");
      },
    });

  const onSubmit = (data) => {
    deposit({
      amount: data.depositAmount,
    });
  };

  const onSubmitTransactionHash = (data) => {
    submitTransactionHash(data);
  };

  return (
    <div>
      {/* header */}
      <header className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <button
            onClick={() => {
              step === 1
                ? navigate(-1)
                : setStep((prev) => (prev > 1 ? prev - 1 : prev));
            }}
            className="bg-primary-50 hover:bg-primary-50/90 flex size-8 items-center justify-center rounded-md"
          >
            <ArrowLeft className="size-6" />
          </button>
          <div className="ml-2 flex items-center gap-2 text-xl font-medium">
            <span className="hidden lg:inline">My Wallet</span>
            <span className="text-primary-50 mt-1.5 hidden lg:inline">
              <ChevronRight size={16} />
            </span>
            <span>Deposit</span>
          </div>
        </div>

        <SearchBar />
      </header>

      <div className="mt-6">
        {/* step indicator */}
        <div className="mx-auto hidden max-w-100 items-center justify-between gap-2 lg:flex">
          <div
            className={`${step >= 1 ? "bg-primary-500 text-primary-100" : "border-primary-50 text-primary-500 border"} flex size-6 items-center justify-center rounded-full`}
          >
            <span className="mb-0.5">1</span>
          </div>
          <div className="border-primary-50 grow border-b" />
          <div
            className={`${step >= 2 ? "bg-primary-500 text-primary-100" : "border-primary-50 text-primary-500 border"} flex size-6 items-center justify-center rounded-full`}
          >
            <span className="mb-0.5">2</span>
          </div>
          <div className="border-primary-50 grow border-b" />
          <div
            className={`${step >= 3 ? "bg-primary-500 text-primary-100" : "border-primary-50 text-primary-500 border"} flex size-6 items-center justify-center rounded-full`}
          >
            <span className="mb-0.5">3</span>
          </div>
        </div>

        {/* step 1 */}
        {step === 1 && (
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="mx-auto mt-8 max-w-120"
          >
            <Form {...form}>
              <FormField
                control={form.control}
                name="token"
                render={({ field }) => (
                  <FormItem className="flex flex-col items-center lg:items-start">
                    <FormLabel>Payment Method</FormLabel>
                    <FormControl>
                      <Select
                        onValueChange={field.onChange}
                        defaultValue={field.value}
                      >
                        <FormControl>
                          <SelectTrigger
                            className={
                              "mx-auto w-42 rounded-full bg-white lg:w-full"
                            }
                          >
                            <SelectValue placeholder="Select payment method" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem
                            className="flex items-center gap-2 font-medium"
                            value={"ERC20-USDT"}
                          >
                            <img
                              className="size-6"
                              src="/images/ercusdt.svg"
                              alt="usdt erc20 icon"
                            />
                            <span>ERC20-USDT</span>
                          </SelectItem>
                          <SelectItem
                            className="flex items-center gap-2 font-medium"
                            value={"ERC20-USDC"}
                          >
                            <img
                              className="size-6"
                              src="/images/usdc.svg"
                              alt="usdc icon"
                            />

                            <span>ERC20-USDC</span>
                          </SelectItem>
                          <SelectItem
                            className="flex items-center gap-2 font-medium"
                            value={"TRC20-USDT"}
                          >
                            <img
                              className="size-6"
                              src="/images/trcusdt.svg"
                              alt="usdt trc20 icon"
                            />
                            <span>TRC20-USDT</span>
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="depositAmount"
                render={({ field }) => (
                  <FormItem className="mt-4 flex flex-col items-center md:items-start">
                    <FormLabel>Deposit Amount</FormLabel>
                    <FormControl>
                      <Input
                        className={
                          "bg-primary-100/15 h-20 w-40 rounded-xl border border-white px-4 py-2.5 text-center text-2xl md:h-auto md:w-full md:rounded-full md:border-none md:bg-white md:text-left"
                        }
                        {...field}
                        placeholder="Input Amount"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <Button
                disabled={isDepositing}
                type="submit"
                className={"mt-6 w-full"}
              >
                {isDepositing ? <Throbber /> : "Proceed"}
              </Button>
            </Form>
          </form>
        )}

        {/* step 2   */}
        {step === 2 && (
          <div className="mx-auto mt-8 max-w-120">
            <p className="text-lg font-medium">You are Depositing</p>
            <p className="text-5xl leading-6 font-bold lg:text-3xl">
              {formatAmount(depositOrder?.amount)}{" "}
              <span className="text-base">USD</span>
            </p>
            <div className="mt-6 flex flex-col gap-4 lg:flex-row lg:justify-between">
              <div className="text-lg">
                <div>
                  <p className="text-primary-500/40">Order No.</p>
                  <div className="flex items-center gap-2">
                    <span className="leading-1 font-medium">
                      {depositOrder?.id}
                    </span>
                    <div className="relative flex items-center justify-center">
                      {isCopied && (
                        <motion.div
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          transition={{ duration: 0.3, ease: "easeOut" }}
                          exit={{ opacity: 0 }}
                          className="absolute bottom-full left-0 z-10 flex h-full items-center justify-center rounded-lg bg-black/80 px-2.5 py-1 text-white"
                        >
                          Copied
                        </motion.div>
                      )}
                      <button
                        onClick={() => {
                          setIsCopied(true);
                          setTimeout(() => setIsCopied(false), 3000);
                          navigator.clipboard.writeText(depositOrder?.id);
                        }}
                        className="shrink-0 cursor-pointer"
                      >
                        <img
                          src="/icons/copy.svg"
                          className="size-6"
                          alt="copy icon"
                        />
                      </button>
                    </div>
                  </div>
                </div>
                <div className="mt-4">
                  <p className="text-primary-500/40">
                    Please deposit to this address only
                  </p>
                  <div className="flex items-center gap-2">
                    <span
                      className="max-w-sm shrink overflow-hidden leading-5.5 font-medium text-ellipsis whitespace-nowrap"
                      title={
                        form.watch("token") === "ERC20-USDC" ||
                        form.watch("token") === "ERC20-USDT"
                          ? "0xfa96639d581c3cbc0ddaa4f065edef4eb77f4967"
                          : "TYpC6EdZeDVHQ64V6vabvcCcV7jghvibGK"
                      }
                    >
                      {form.watch("token") === "ERC20-USDC" ||
                      form.watch("token") === "ERC20-USDT"
                        ? "0xfa96639d581c3cbc0ddaa4f065edef4eb77f4967"
                        : "TYpC6EdZeDVHQ64V6vabvcCcV7jghvibGK"}
                    </span>
                    <Copy
                      text={
                        form.watch("token") === "ERC20-USDC" ||
                        form.watch("token") === "ERC20-USDT"
                          ? "0xfa96639d581c3cbc0ddaa4f065edef4eb77f4967"
                          : "TYpC6EdZeDVHQ64V6vabvcCcV7jghvibGK"
                      }
                    />
                  </div>
                </div>
              </div>

              <div className="self-start rounded-md bg-white p-4 text-center lg:shrink-0">
                <img
                  src={
                    form.watch("token") === "ERC20-USDC" ||
                    form.watch("token") === "ERC20-USDT"
                      ? "/images/usdc-erc20qr.png"
                      : "/images/usdt-trcqr.png"
                  }
                  alt="qr code"
                  className="size-20"
                />
                <span className="text-primary-50">or scan this</span>
              </div>
            </div>

            <div className="mt-4 text-lg leading-6">
              <h4 className="font-semibold">Notice</h4>
              <p>
                If the amount you transfer is not the same as the order amount
                stated above, your account will not be credited
              </p>
              <ol className="text-primary-500/40 list-decimal pl-4">
                <li>
                  If there is an amount after the decimal point, it must also be
                  transferred.
                </li>
                <li>
                  Some exchanges may deduct handling fees for withdrawals.
                  Please make sure that the amount you transfer to the wallet
                  above is the same as the order amount after fees have been
                  deducted from your external wallet
                </li>
              </ol>
            </div>

            <div className="mt-10 text-center">
              <p className="mx-auto max-w-[300px] leading-4 font-medium">
                This payment will expire soon, please send your payment within
              </p>

              <div className="mt-2 flex items-center justify-center gap-2 text-lg font-medium">
                <span>
                  <span className="text-3xl">{countdown.hours}</span>hr
                </span>
                <span>
                  <span className="text-3xl">{countdown.minutes}</span>min
                </span>
                <span>
                  <span className="text-3xl">{countdown.seconds}</span>sec
                </span>
              </div>
              <form
                onSubmit={transactionForm.handleSubmit(onSubmitTransactionHash)}
              >
                <Form {...transactionForm}>
                  <FormField
                    control={transactionForm.control}
                    name="transactionHash"
                    render={({ field }) => (
                      <FormItem className={"mt-2 flex flex-col items-start"}>
                        <FormLabel>Transaction ID</FormLabel>
                        <FormControl>
                          <Input
                            placeholder="Enter Transaction ID"
                            className="rounded-2xl bg-white"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <div className="mt-2 text-left text-sm leading-4">
                    <p className="">
                      <span className="text-primary-red font-medium">
                        Note:
                      </span>{" "}
                      Please ensure you enter the correct transaction ID to
                      complete your transaction successfully.
                    </p>
                  </div>
                  <Button
                    disabled={isSubmittingHash}
                    type="submit"
                    className={"mt-6 w-full"}
                  >
                    {isSubmittingHash ? <Throbber /> : "Proceed"}
                  </Button>
                </Form>
              </form>
            </div>
          </div>
        )}

        {/* step 3 */}
        {step === 3 && (
          <div className="mx-auto mt-8 lg:max-w-120">
            {transactionState === "pending" && (
              <>
                <div className="bg-primary-500 relative mx-auto flex size-8 shrink-0 items-center justify-center rounded-full">
                  <div className="border-primary-100 size-6 animate-spin rounded-full border-2 border-t-transparent border-l-transparent" />
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="border-primary-50 size-4 animate-spin rounded-full border-2 border-r-transparent border-b-transparent" />
                  </div>
                </div>
                <div className="text-primary-500/40 text-center text-sm font-medium">
                  Pending
                </div>
                <div className="mt-8 text-lg">
                  <div className="flex items-center justify-between">
                    <span className="text-primary-500/40">Id</span>
                    <span className="font-medium">{depositOrderData.id}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-primary-500/40">Currency</span>
                    <span className="font-medium">USDT</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-primary-500/40">Deposit Amount</span>
                    <span className="font-medium">
                      {depositOrderData.amount}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-primary-500/40">Network</span>
                    <span className="font-medium">TRC20</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-primary-500/40">Status</span>
                    <span className="font-medium">Pending</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-primary-500/40">Time</span>
                    <span className="font-medium">
                      {new Date(depositOrderData.updatedAt).toLocaleString()}
                    </span>
                  </div>
                </div>
              </>
            )}
            {transactionState === "success" && (
              <div className="text-center text-lg">
                <img
                  src="/icons/success.svg"
                  className="mx-auto size-10"
                  alt=""
                />
                <p className="mt-4 font-medium">
                  Your transaction was successful
                </p>
                <Button
                  onClick={() => navigate("/dashboard/wallet")}
                  className="mx-auto mt-6 w-full"
                >
                  Done
                </Button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Deposit;
